import type { LocalizedString, ProductListItem } from "@repo/shared/types";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

type PlatformProductPickerProps = {
  products: ProductListItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  labels: { selectAll: string; noProducts: string; help: string };
  getLabel: (i18n: LocalizedString) => string;
};

/**
 * Checkbox list of the collection's products with a "select all" control —
 * the platform dialog's product-membership picker (QP-226).
 */
export function PlatformProductPicker({
  products,
  selectedIds,
  onChange,
  disabled,
  labels,
  getLabel,
}: PlatformProductPickerProps) {
  if (products.length === 0) {
    return <p className="text-xs text-muted-foreground">{labels.noProducts}</p>;
  }

  const selected = new Set(selectedIds);
  const allSelected = products.every((p) => selected.has(p.id));
  const someSelected = selectedIds.length > 0;

  const toggle = (productId: string) => {
    onChange(
      selected.has(productId)
        ? selectedIds.filter((id) => id !== productId)
        : [...selectedIds, productId],
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 pb-2 border-b text-sm">
        <Checkbox
          id="platform-products-select-all"
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={(checked) => onChange(checked ? products.map((p) => p.id) : [])}
          disabled={disabled}
        />
        <Label htmlFor="platform-products-select-all" className="font-normal cursor-pointer">
          {labels.selectAll}
        </Label>
      </div>
      <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
        {products.map((product) => (
          <div key={product.id} className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              id={`platform-product-${product.id}`}
              checked={selected.has(product.id)}
              onCheckedChange={() => toggle(product.id)}
              disabled={disabled}
            />
            <Label
              htmlFor={`platform-product-${product.id}`}
              className="font-normal cursor-pointer"
            >
              {getLabel(product.nameI18n)}
            </Label>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{labels.help}</p>
    </>
  );
}
